import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Leaf, ChefHat, Clock, ShoppingBag, Check, MapPin, Navigation, ExternalLink } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import SEOHead from "@/components/SEOHead";
import GeoMessage from "@/components/GeoMessage";
import FAQSection from "@/components/FAQSection";
import { useLanguage } from "@/i18n/LanguageContext";
import { useExternalLink } from "@/contexts/ExternalLinkContext";

import heroImg from "@/assets/hero-sushi.webp";
import nigiriImg from "@/assets/dish-nigiri.webp";
import dragonImg from "@/assets/dish-dragon-roll.webp";
import sashimiImg from "@/assets/dish-sashimi.webp";
import tempuraImg from "@/assets/dish-tempura.webp";
import mochiImg from "@/assets/dish-mochi.webp";
import interiorImg from "@/assets/restaurant-interior.webp";
import chefImg from "@/assets/chef-preparing.webp";
import specialImg from "@/assets/dish-special.webp";
import mainImg from "@/assets/dish-main.webp";
import gallery1 from "@/assets/gallery-1.webp";
import gallery3 from "@/assets/gallery-3.webp";

const RESERVE_URL = "https://www.foodbooking.com/ordering/?restaurant_uid=b1c06bc6-d5a3-43d4-82d8-10720b240538&reservation=true";
const ORDER_URL = "https://www.foodbooking.com/ordering/restaurant/menu?restaurant_uid=b1c06bc6-d5a3-43d4-82d8-10720b240538";
const GOOGLE_REVIEW_URL = "https://search.google.com/local/writereview?placeid=ChIJvVeVn86ZpBIRpluPwn56EXs";
const GOOGLE_MAPS_EMBED = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2994.074!2d2.1324!3d41.3756!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a499ce9f9557bd%3A0x7b117a7ec28f5ba6!2sSushi%20Chibi%20Kokoro!5e0!3m2!1ses!2ses!4v1700000000000!5m2!1ses!2ses";
const GOOGLE_MAPS_DIRECTIONS = "https://maps.google.com/?cid=8868003826117860262";

const featuredDishes = [
  { name: "Nigiri de Salmón", price: "4,50 €", img: nigiriImg, desc: "Salmón fresco sobre arroz" },
  { name: "Dragon Roll", price: "14,90 €", img: dragonImg, desc: "Aguacate, langostino, anguila" },
  { name: "Sashimi de Atún", price: "12,50 €", img: sashimiImg, desc: "Atún rojo cortado a mano" },
  { name: "Tempura de Langostino", price: "10,90 €", img: tempuraImg, desc: "Crujiente y ligero" },
  { name: "Mochi Ice Cream", price: "5,50 €", img: mochiImg, desc: "Helado japonés artesanal" },
];

const reviews = [
  { name: "María G.", text: "El mejor sushi de Barcelona, sin duda. Siempre fresco y con presentaciones impecables.", rating: 5 },
  { name: "Carlos R.", text: "Ambiente increíble y atención personalizada. Se nota la pasión por la cocina japonesa.", rating: 5 },
  { name: "Laura P.", text: "Los rolls son espectaculares. Repetimos cada semana, nunca decepciona.", rating: 5 },
  { name: "Andrés M.", text: "Calidad-precio excelente. El menú de mediodía es una joya escondida en Sants.", rating: 5 },
  { name: "Sophie T.", text: "We found this gem near Sants station. Best Japanese buffet in Barcelona!", rating: 5 },
];

const Index = () => {
  const { t } = useLanguage();
  const { openLink } = useExternalLink();

  const benefits = [
    { icon: Leaf, title: t("benefit_fresh"), desc: t("benefit_fresh_desc") },
    { icon: ChefHat, title: t("benefit_authentic"), desc: t("benefit_authentic_desc") },
    { icon: Star, title: t("benefit_cozy"), desc: t("benefit_cozy_desc") },
    { icon: ShoppingBag, title: t("benefit_online"), desc: t("benefit_online_desc") },
  ];

  const buffetPoints = [
    t("buffet_point_1"),
    t("buffet_point_2"),
    t("buffet_point_3"),
    t("buffet_point_4"),
  ];

  return (
    <>
      <SEOHead
        title={t("seo_home_title")}
        description={t("seo_home_desc")}
        canonical="https://chibikokoro.es/"
      />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({ "@context": "https://schema.org", "@type": "WebPage", speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "[data-speakable]"] } }) }} />


      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Restaurant",
            name: "Chibikokoro – Japanese Restaurant",
            description: "Restaurante japonés en Barcelona con buffet libre desde 16,99€ y menú diario por 13,50€. Sushi fresco cerca de la estación de Sants y Plaça d'Espanya.",
            servesCuisine: "Japanese",
            image: "https://chibikokoro.es/chibi-logo.png",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Carrer de Béjar, 64, Sants-Montjuïc, cerca de la estación de Sants",
              postalCode: "08014",
              addressLocality: "Barcelona",
              addressRegion: "Cataluña",
              addressCountry: "ES",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: 41.3781,
              longitude: 2.1445,
            },
            telephone: ["+34931463045", "+34653538328"],
            email: "sushichibikokoro@gmail.com",
            url: "https://chibikokoro.es",
            menu: "https://chibikokoro.es/menu",
            acceptsReservations: "True",
            reservations: RESERVE_URL,
            priceRange: "€€",
            currenciesAccepted: "EUR",
            paymentAccepted: "Cash, Credit Card",
            openingHoursSpecification: {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              opens: "13:00",
              closes: "23:00",
            },
            sameAs: [
              "https://www.facebook.com/chibikokoro.kokoro",
              "https://www.instagram.com/chibikokoro/",
            ],
            aggregateRating: { "@type": "AggregateRating", ratingValue: "4.0", bestRating: "5", reviewCount: "1100" },
            hasMenu: {
              "@type": "Menu",
              name: "Carta Chibikokoro",
              url: "https://chibikokoro.es/menu",
              hasMenuSection: [
                { "@type": "MenuSection", name: "Buffet Libre Mediodía", description: "Buffet libre japonés por 16,99€ por persona, lunes a viernes de 13:00 a 18:00" },
                { "@type": "MenuSection", name: "Buffet Libre Noche/Fines de Semana", description: "Buffet libre japonés por 19,99€ por persona, noches, fines de semana y festivos" },
                { "@type": "MenuSection", name: "Menú Diario", description: "Menú diario por 13,50€ con bebida y postre incluidos, lunes a viernes de 13:00 a 16:00" },
              ],
            },
          }),
        }}
      />

      {/* HERO */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
        <img src={heroImg} alt="Sushi premium en Chibikokoro, restaurante japonés en Barcelona cerca de la estación de Sants" className="absolute inset-0 w-full h-full object-cover" loading="eager" fetchPriority="high" />
        <div className="absolute inset-0" style={{ background: "var(--hero-overlay)" }} />
        <div className="relative z-10 text-center px-5 max-w-3xl">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="text-xs md:text-sm tracking-[0.4em] uppercase text-shiro/60 font-body mb-4">
            {t("hero_subtitle")}
          </motion.p>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-light text-shiro text-shadow-hero leading-tight">
            {t("hero_title")}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }} className="mt-6 text-shiro/70 text-base md:text-lg font-body max-w-xl mx-auto">
            {t("hero_desc")}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8 }} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={() => openLink(RESERVE_URL)} className="bg-primary text-primary-foreground text-xs tracking-widest uppercase px-10 py-4 hover:bg-primary/90 transition-colors font-body w-full sm:w-auto text-center active:scale-[0.97]">
              {t("cta_reserve_table")}
            </button>
            <button onClick={() => openLink(ORDER_URL)} className="border border-shiro/30 text-shiro text-xs tracking-widest uppercase px-10 py-4 hover:bg-shiro/10 transition-colors font-body w-full sm:w-auto text-center active:scale-[0.97]">
              {t("cta_order_online")}
            </button>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1 }} className="mt-4">
            <Link to="/menu" className="text-shiro/50 text-xs tracking-widest uppercase font-body hover:text-shiro/80 transition-colors">
              {t("cta_view_menu")} →
            </Link>
          </motion.div>
        </div>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }} className="absolute bottom-8 left-1/2 -translate-x-1/2 w-px h-12 bg-gradient-to-b from-shiro/0 via-shiro/40 to-shiro/0" />
      </section>

      {/* GEO MESSAGE */}
      <GeoMessage />

      {/* THE EXPERIENCE */}
      <section className="section-padding bg-washi">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <img src={chefImg} alt="Chef preparando sushi fresco en Chibikokoro Barcelona" className="w-full aspect-[4/5] object-cover" loading="lazy" />
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <span className="text-xs tracking-[0.3em] uppercase text-muted-foreground font-body">{t("about_label")}</span>
            <h2 className="font-display text-3xl md:text-5xl font-light mt-2 mb-6">{t("about_title")}</h2>
            <p className="text-muted-foreground leading-relaxed font-body mb-4">{t("about_p1")}</p>
            <p className="text-muted-foreground leading-relaxed font-body mb-8">{t("about_p2")}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/nosotros" aria-label="Learn more about Chibikokoro Japanese Restaurant" className="text-xs tracking-widest uppercase font-body text-foreground border-b border-foreground pb-1 hover:text-aka hover:border-aka transition-colors">
                {t("about_more")}
              </Link>
              <button onClick={() => openLink(RESERVE_URL)} className="text-xs tracking-widest uppercase font-body text-aka border-b border-aka pb-1 hover:text-foreground hover:border-foreground transition-colors text-left">
                {t("cta_reserve_now")} →
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BUFFET & DAILY MENU */}
      <section className="section-padding">
        <SectionHeading subtitle={t("buffet_section_label")} title={t("buffet_section_title")} />
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="bg-kuro text-shiro p-8 md:p-10 text-center">
            <h3 className="font-display text-2xl md:text-3xl font-light mb-4">{t("menu_buffet_title")}</h3>
            <div className="mb-4">
              <p className="font-display text-4xl md:text-5xl font-semibold text-aka mb-1">16,99€</p>
              <p className="text-shiro/50 text-sm font-body tracking-wider">{t("buffet_per_person")}</p>
              <div className="flex items-center justify-center gap-2 text-shiro/40 text-xs font-body mt-1">
                <Clock className="w-3.5 h-3.5" />
                <span>{t("menu_buffet_schedule_lunch")}</span>
              </div>
            </div>
            <div className="w-8 h-px bg-shiro/20 mx-auto my-4" />
            <div className="mb-4">
              <p className="font-display text-3xl md:text-4xl font-semibold text-aka/80 mb-1">19,99€</p>
              <p className="text-shiro/50 text-sm font-body tracking-wider">{t("buffet_per_person")}</p>
              <div className="flex items-center justify-center gap-2 text-shiro/40 text-xs font-body mt-1">
                <Clock className="w-3.5 h-3.5" />
                <span>{t("menu_buffet_schedule_dinner")}</span>
              </div>
            </div>
            <div className="w-8 h-px bg-shiro/20 mx-auto my-4" />
            <p className="text-shiro/60 text-sm font-body mb-6">{t("buffet_subtitle")}</p>
            <ul className="space-y-3 text-left max-w-xs mx-auto mb-6">
              {buffetPoints.map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-shiro/70 text-sm font-body">
                  <Check className="w-4 h-4 text-aka shrink-0 mt-0.5" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
            <p className="text-shiro/50 text-xs font-body">{t("menu_buffet_kids_note")}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }} className="border border-border p-8 md:p-10 text-center">
            <h3 className="font-display text-2xl md:text-3xl font-light mb-4">{t("menu_daily_title")}</h3>
            <p className="font-display text-4xl md:text-5xl font-semibold text-aka mb-1">13,50€</p>
            <p className="text-muted-foreground text-sm font-body tracking-wider">{t("buffet_per_person")}</p>
            <div className="w-8 h-px bg-border mx-auto my-6" />
            <p className="text-muted-foreground text-sm font-body mb-4">{t("menu_daily_note")}</p>
            <p className="text-muted-foreground/80 text-sm font-body mb-6">{t("daily_ideal")}</p>
            <div className="flex items-center justify-center gap-2 text-muted-foreground/60 text-xs font-body">
              <Clock className="w-3.5 h-3.5" />
              <span>{t("menu_daily_schedule")}</span>
            </div>
          </motion.div>
        </div>
        <div className="text-center mt-10">
          <button onClick={() => openLink(RESERVE_URL)} className="bg-primary text-primary-foreground text-xs tracking-widest uppercase px-10 py-4 hover:bg-primary/90 transition-colors font-body active:scale-[0.97]">
            {t("cta_reserve_table")}
          </button>
        </div>
      </section>

      {/* FEATURED DISHES */}
      <section className="section-padding bg-washi">
        <SectionHeading subtitle={t("featured_label")} title={t("featured_title")} />
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {featuredDishes.map((dish, i) => (
            <motion.div key={dish.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="group">
              <Link to="/menu" className="block overflow-hidden">
                <img src={dish.img} alt={`${dish.name} – sushi en Barcelona, Chibikokoro`} className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              </Link>
              <div className="pt-4 flex justify-between items-start">
                <div>
                  <h3 className="font-display text-xl">{dish.name}</h3>
                  <p className="text-muted-foreground text-sm font-body mt-1">{dish.desc}</p>
                </div>
                <span className="text-aka font-display text-lg font-semibold whitespace-nowrap ml-4">{dish.price}</span>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/menu" className="text-xs tracking-widest uppercase font-body text-foreground border-b border-foreground pb-1 hover:text-aka hover:border-aka transition-colors">
            {t("featured_see_all")}
          </Link>
        </div>
      </section>

      {/* WHY CHOOSE US — LOCAL AUTHORITY */}
      <section className="section-padding bg-kuro">
        <SectionHeading subtitle={t("benefits_label")} title={t("local_why_title")} light />
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {benefits.map((b, i) => (
            <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
              <b.icon className="w-8 h-8 text-aka mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-display text-xl text-shiro mb-2">{b.title}</h3>
              <p className="text-shiro/50 text-sm font-body leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.4 }} className="max-w-3xl mx-auto text-center mt-16">
          <p className="text-shiro/50 text-sm font-body leading-relaxed">
            {t("local_authority_text")}
          </p>
        </motion.div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="section-padding">
        <SectionHeading subtitle={t("gallery_label")} title={t("gallery_title")} />
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
          {[nigiriImg, gallery1, sashimiImg, gallery3, specialImg, mainImg].map((img, i) => (
            <motion.div key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="overflow-hidden">
              <img src={img} alt={`Galería Chibikokoro – restaurante japonés Barcelona ${i + 1}`} className="w-full aspect-square object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/galeria" className="text-xs tracking-widest uppercase font-body text-foreground border-b border-foreground pb-1 hover:text-aka hover:border-aka transition-colors">
            {t("gallery_see_all")}
          </Link>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="section-padding bg-washi">
        <SectionHeading subtitle={t("reviews_label")} title={t("reviews_title")} />
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
          {reviews.slice(0, 4).map((r, i) => (
            <motion.div key={r.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="bg-background p-8">
              <div className="flex gap-1 mb-4">
                {Array.from({ length: r.rating }).map((_, j) => (
                  <Star key={j} className="w-4 h-4 fill-kin text-kin" />
                ))}
              </div>
              <p className="text-foreground/80 font-body leading-relaxed italic mb-4">"{r.text}"</p>
              <p className="text-xs tracking-widest uppercase text-muted-foreground font-body">{r.name}</p>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-10 space-y-4">
          <div className="inline-flex items-center gap-2 text-muted-foreground text-sm font-body">
            <Star className="w-5 h-5 fill-kin text-kin" />
            <span className="font-display text-lg text-foreground font-semibold">4</span>
            <span>/ 5 — 1100+ {t("reviews_on_google")}</span>
          </div>
          <div>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs tracking-widest uppercase font-body text-aka border-b border-aka pb-1 hover:text-foreground hover:border-foreground transition-colors"
            >
              {t("reviews_leave_review")} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </section>

      {/* LOCATION + MAP */}
      <section className="section-padding">
        <SectionHeading subtitle={t("location_label")} title={t("location_title")} />
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="space-y-4">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-aka mt-1 shrink-0" />
              <div>
                <p className="font-body text-lg">Carrer de Béjar, 64, Sants-Montjuïc</p>
                <p className="font-body text-lg">08014 Barcelona</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground/70 text-sm font-body">
              <Clock className="w-4 h-4 text-aka" />
              <span>{t("contact_days")} · {t("contact_schedule")}</span>
            </div>
            <p className="text-muted-foreground text-sm font-body">{t("local_near_sants")}</p>
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <a
                href={GOOGLE_MAPS_DIRECTIONS}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground text-xs tracking-widest uppercase px-8 py-3 hover:bg-primary/90 transition-colors font-body active:scale-[0.97]"
              >
                <Navigation className="w-4 h-4" />
                {t("local_directions")}
              </a>
              <button
                onClick={() => openLink(RESERVE_URL)}
                className="inline-flex items-center justify-center gap-2 border border-border text-xs tracking-widest uppercase px-8 py-3 hover:bg-accent transition-colors font-body active:scale-[0.97]"
              >
                {t("cta_reserve_now")}
              </button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="w-full aspect-[4/3]">
              <iframe
                title="Ubicación Chibikokoro – Restaurante Japonés Barcelona"
                src={GOOGLE_MAPS_EMBED}
                className="w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ — AEO */}
      <FAQSection />

      {/* FINAL CTA */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <img src={interiorImg} alt="Interior del restaurante japonés Chibikokoro en Barcelona Sants" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-kuro/70" />
        <div className="relative z-10 text-center px-5">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <h2 className="font-display text-3xl md:text-5xl text-shiro font-light mb-6">{t("cta_banner_title")}</h2>
            <p className="text-shiro/60 font-body mb-10 max-w-md mx-auto">{t("cta_banner_desc")}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => openLink(RESERVE_URL)} className="bg-primary text-primary-foreground text-xs tracking-widest uppercase px-12 py-4 hover:bg-primary/90 transition-colors font-body active:scale-[0.97]">
                {t("cta_reserve_table")}
              </button>
              <button onClick={() => openLink(ORDER_URL)} className="border border-shiro/30 text-shiro text-xs tracking-widest uppercase px-12 py-4 hover:bg-shiro/10 transition-colors font-body active:scale-[0.97]">
                {t("cta_order_online")}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="h-16" />
    </>
  );
};

export default Index;
